import React, { ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Filter,
  FilterDropdown,
  FilterDropdownItem,
  Icon,
  IconButton,
  PageHeading,
  Pills,
  Table,
  Text,
  Tooltip,
} from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';
import {
  CaretDown,
  Export,
  Info,
  LinkBreak,
  LinkSimple,
  MagnifyingGlass,
  X,
  type IconProps as PhosphorIconProps,
} from '@phosphor-icons/react';
import { PortalPayfacsLayout } from '../PortalPayfacsLayout';
import { DrawerOperacoesEmLote } from '../../backoffice/POS/DrawerOperacoesEmLote';
import type { OperationType } from '../../backoffice/POS/DrawerOperacoesEmLote';
import { DrawerDetalhesTerminal } from './DrawerDetalhesTerminal';
import { isTransactionActive } from '../../_shared/terminalUtils';
import { ExportModal } from '../../_shared/ExportModal';
import './Terminals.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type TerminalStatus =
  | 'Alocada'
  | 'Em estoque';

type TerminalOwner = 'Barte' | 'Própria';

interface Terminal {
  id: string;
  serialNumber: string;
  status: TerminalStatus;
  payfac: string;
  payfacId: string;
  owner: TerminalOwner;
  seller: string;
  lastTransaction: string;
  sellerId: string;
  terminalId: string;
  posVersion: string;
  vinculationDate: string;
  model: string;
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const ALL_TERMINALS: Terminal[] = [
  { id: 'TRM-001', serialNumber: 'SN-20240001', status: 'Alocada',    payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: 'Loja Central LTDA',       lastTransaction: '10/03/2026 14:32', sellerId: 'SEL-001', terminalId: 'TRM-0001', posVersion: '3.2.1', vinculationDate: '15/01/2025', model: 'PAX A920' },
  { id: 'TRM-002', serialNumber: 'SN-20240002', status: 'Alocada',    payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: 'Mercado Bom Preço',        lastTransaction: '10/03/2026 11:15', sellerId: 'SEL-002', terminalId: 'TRM-0002', posVersion: '3.2.1', vinculationDate: '03/03/2025', model: 'PAX A930' },
  { id: 'TRM-003', serialNumber: 'SN-20240003', status: 'Em estoque', payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Própria',  seller: '—',                        lastTransaction: '—',               sellerId: '—',       terminalId: 'TRM-0003', posVersion: '3.1.0', vinculationDate: '—',          model: 'Ingenico Move 5000' },
  { id: 'TRM-004', serialNumber: 'SN-20240004', status: 'Em estoque', payfac: 'FinTech BR', payfacId: 'PAYFAC-002', owner: 'Própria',  seller: 'Restaurante Sabor Bom',    lastTransaction: '05/03/2026 09:00', sellerId: 'SEL-004', terminalId: 'TRM-0004', posVersion: '3.0.5', vinculationDate: '22/06/2024', model: 'Verifone V240m' },
  { id: 'TRM-005', serialNumber: 'SN-20240005', status: 'Alocada',    payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: 'Posto Combustível Star',   lastTransaction: '10/03/2026 10:20', sellerId: 'SEL-005', terminalId: 'TRM-0005', posVersion: '3.2.0', vinculationDate: '10/08/2025', model: 'PAX A920' },
  { id: 'TRM-006', serialNumber: 'SN-20240006', status: 'Alocada',    payfac: 'PayNow BR',  payfacId: 'PAYFAC-003', owner: 'Própria',  seller: 'Academia FitLife',         lastTransaction: '09/03/2026 17:45', sellerId: 'SEL-006', terminalId: 'TRM-0006', posVersion: '3.2.1', vinculationDate: '14/11/2024', model: 'PAX A930' },
  { id: 'TRM-007', serialNumber: 'SN-20240007', status: 'Em estoque', payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: '—',                        lastTransaction: '01/02/2026 08:30', sellerId: '—',       terminalId: 'TRM-0007', posVersion: '3.0.5', vinculationDate: '—',          model: 'Ingenico Move 5000' },
  { id: 'TRM-008', serialNumber: 'SN-20240008', status: 'Alocada',    payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: 'Padaria Pão Quente',       lastTransaction: '10/03/2026 08:10', sellerId: 'SEL-008', terminalId: 'TRM-0008', posVersion: '3.2.1', vinculationDate: '01/04/2025', model: 'Positivo L300' },
  { id: 'TRM-009', serialNumber: 'SN-20240009', status: 'Em estoque', payfac: 'FinTech BR', payfacId: 'PAYFAC-002', owner: 'Própria',  seller: 'TechStore Informática',    lastTransaction: '20/02/2026 13:22', sellerId: 'SEL-009', terminalId: 'TRM-0009', posVersion: '3.1.0', vinculationDate: '19/09/2024', model: 'Verifone V240m' },
  { id: 'TRM-010', serialNumber: 'SN-20240010', status: 'Alocada',    payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: 'Supermercado Família',     lastTransaction: '10/03/2026 16:55', sellerId: 'SEL-010', terminalId: 'TRM-0010', posVersion: '3.2.1', vinculationDate: '07/12/2024', model: 'PAX A920' },
  { id: 'TRM-011', serialNumber: 'SN-20240011', status: 'Em estoque', payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Própria',  seller: '—',                        lastTransaction: '—',               sellerId: '—',       terminalId: 'TRM-0011', posVersion: '3.2.0', vinculationDate: '—',          model: 'Ingenico Move 5000' },
  { id: 'TRM-012', serialNumber: 'SN-20240012', status: 'Alocada',    payfac: 'PayNow BR',  payfacId: 'PAYFAC-003', owner: 'Própria',  seller: 'Farmácia Saúde & Vida',    lastTransaction: '08/03/2026 10:00', sellerId: 'SEL-012', terminalId: 'TRM-0012', posVersion: '3.1.0', vinculationDate: '25/02/2025', model: 'PAX A930' },
  { id: 'TRM-013', serialNumber: 'SN-20240013', status: 'Alocada',    payfac: 'FinTech BR', payfacId: 'PAYFAC-002', owner: 'Barte',    seller: 'Livraria Cultura',         lastTransaction: '09/03/2026 14:05', sellerId: 'SEL-013', terminalId: 'TRM-0013', posVersion: '3.2.1', vinculationDate: '18/07/2025', model: 'Positivo L300' },
  { id: 'TRM-014', serialNumber: 'SN-20240014', status: 'Em estoque', payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Própria',  seller: '—',                        lastTransaction: '—',               sellerId: '—',       terminalId: 'TRM-0014', posVersion: '3.2.1', vinculationDate: '—',          model: 'Verifone V240m' },
  { id: 'TRM-015', serialNumber: 'SN-20240015', status: 'Alocada',    payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: 'Ótica Visão Clara',        lastTransaction: '09/03/2026 15:30', sellerId: 'SEL-015', terminalId: 'TRM-0015', posVersion: '3.2.1', vinculationDate: '30/10/2024', model: 'PAX A920' },
  { id: 'TRM-016', serialNumber: 'SN-20240016', status: 'Alocada',    payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: 'Pet Shop Animal Love',     lastTransaction: '07/03/2026 11:00', sellerId: 'SEL-016', terminalId: 'TRM-0016', posVersion: '3.0.5', vinculationDate: '05/06/2025', model: 'Positivo L300' },
  { id: 'TRM-017', serialNumber: 'SN-20240017', status: 'Em estoque', payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Própria',  seller: '—',                        lastTransaction: '—',               sellerId: '—',       terminalId: 'TRM-0017', posVersion: '3.2.0', vinculationDate: '—',          model: 'PAX A920' },
  { id: 'TRM-018', serialNumber: 'SN-20240018', status: 'Em estoque', payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: '—',                        lastTransaction: '15/01/2026 09:10', sellerId: '—',       terminalId: 'TRM-0018', posVersion: '3.1.0', vinculationDate: '—',          model: 'Ingenico Move 5000' },
  { id: 'TRM-019', serialNumber: 'SN-20240019', status: 'Alocada',    payfac: 'PayNow BR',  payfacId: 'PAYFAC-003', owner: 'Própria',  seller: 'Consultório Dr. Lima',     lastTransaction: '10/03/2026 09:45', sellerId: 'SEL-019', terminalId: 'TRM-0019', posVersion: '3.2.1', vinculationDate: '12/04/2025', model: 'PAX A930' },
  { id: 'TRM-020', serialNumber: 'SN-20240020', status: 'Em estoque', payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: '—',                        lastTransaction: '—',               sellerId: '—',       terminalId: 'TRM-0020', posVersion: '3.2.1', vinculationDate: '—',          model: 'Verifone V240m' },
  { id: 'TRM-021', serialNumber: 'SN-20240021', status: 'Alocada',    payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Própria',  seller: 'Sorveteria Gelato',        lastTransaction: '10/03/2026 13:20', sellerId: 'SEL-021', terminalId: 'TRM-0021', posVersion: '3.2.1', vinculationDate: '28/08/2025', model: 'PAX A920' },
  { id: 'TRM-022', serialNumber: 'SN-20240022', status: 'Em estoque', payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Barte',    seller: 'Auto Peças Brasil',        lastTransaction: '02/03/2026 08:00', sellerId: 'SEL-022', terminalId: 'TRM-0022', posVersion: '3.0.5', vinculationDate: '03/05/2024', model: 'Positivo L300' },
  { id: 'TRM-023', serialNumber: 'SN-20240023', status: 'Alocada',    payfac: 'Barte',      payfacId: 'PAYFAC-001', owner: 'Própria',  seller: 'Hortifruti Verde Vida',    lastTransaction: '28/02/2026 10:15', sellerId: 'SEL-023', terminalId: 'TRM-0023', posVersion: '3.2.1', vinculationDate: '11/09/2024', model: 'Ingenico Move 5000' },
  { id: 'TRM-024', serialNumber: 'SN-20240024', status: 'Alocada',    payfac: 'FinTech BR', payfacId: 'PAYFAC-002', owner: 'Barte',    seller: 'Barbearia Corte Certo',    lastTransaction: '15/02/2026 14:30', sellerId: 'SEL-024', terminalId: 'TRM-0024', posVersion: '3.1.0', vinculationDate: '17/03/2025', model: 'Verifone V240m' },
  { id: 'TRM-025', serialNumber: 'SN-20240025', status: 'Alocada',    payfac: 'PayNow BR',  payfacId: 'PAYFAC-003', owner: 'Própria',  seller: 'Lavanderia Express',       lastTransaction: '20/02/2026 09:00', sellerId: 'SEL-025', terminalId: 'TRM-0025', posVersion: '3.2.0', vinculationDate: '22/01/2025', model: 'PAX A930' },
];

// Mock de CNPJ do lojista, derivado do Seller ID (não há campo de CNPJ no mock de terminais)
function sellerCnpjFor(sellerId: string): string {
  if (sellerId === '—') return '—';
  const digits = sellerId.replace(/[^0-9]/g, '').padStart(3, '0');
  return `12.${digits}.678/0001-90`;
}

// ─── Search type ──────────────────────────────────────────────────────────────

type SearchType = 'serial' | 'lojista' | 'cnpj' | 'lojistaId';

const SEARCH_TYPE_OPTIONS: { value: SearchType; label: string; placeholder: string }[] = [
  { value: 'serial',    label: 'Serial Number',   placeholder: 'Buscar por Serial Number...' },
  { value: 'lojista',   label: 'Nome do Lojista', placeholder: 'Buscar por nome do lojista...' },
  { value: 'cnpj',      label: 'CNPJ',            placeholder: 'Buscar por CNPJ...'           },
  { value: 'lojistaId', label: 'Id do Lojista',   placeholder: 'Buscar por ID do lojista...'  },
];

// ─── Bulk action logic ────────────────────────────────────────────────────────

// No portal payfac, vincular-payfac não existe — o payfac é sempre o intermediador logado
type BulkActionKey = 'vincular-seller' | 'desvincular';

interface BulkActionDef {
  key: BulkActionKey;
  label: string;
  icon: ComponentType<PhosphorIconProps>;
  operation: OperationType;
  drawerStep: number;
}

const BULK_ACTION_DEFS: Record<BulkActionKey, BulkActionDef> = {
  'vincular-seller': { key: 'vincular-seller', label: 'Vincular a outro lojista', icon: LinkSimple, operation: 'vincular',    drawerStep: 3 },
  'desvincular':     { key: 'desvincular',     label: 'Desvincular',              icon: LinkBreak,  operation: 'desvincular', drawerStep: 3 },
};

// ─── Table columns ────────────────────────────────────────────────────────────

type PillsStateValue = 'success' | 'info' | 'warning' | 'error' | 'default' | 'accent' | 'alert';

const STATUS_PILL: Record<TerminalStatus, PillsStateValue> = {
  'Alocada':    'success',
  'Em estoque': 'warning',
};

const STATUS_LABEL: Record<TerminalStatus, string> = {
  'Alocada':    'Alocada Lojista',
  'Em estoque': 'Estoque',
};


const BASE_COLUMNS: ColumnDefinition<Terminal>[] = [
  {
    key: 'id',
    title: 'Terminal',
    dataIndex: 'id',
    width: '16%',
    render: (_value, record) => (
      <div className="pos-cell">
        <Text variant="label-sm">{record.id}</Text>
        <Text variant="body-xs" className="pos-serial">{record.serialNumber}</Text>
      </div>
    ),
  },
  {
    key: 'owner',
    title: 'Proprietário',
    dataIndex: 'owner',
    width: '12%',
    render: (value) => (
      <Pills
        label={value as string}
        state="default"
        variant={value === 'Barte' ? 'fill' : 'light'}
        size="sm"
      />
    ),
  },
  {
    key: 'seller',
    title: 'Lojista',
    dataIndex: 'seller',
    width: '32%',
    render: (_value, record) => {
      if (record.status !== 'Alocada') return '—';
      return (
        <div className="pos-cell">
          <Text variant="label-sm">{record.seller}</Text>
          <Text variant="body-xs" className="pos-serial">{record.sellerId}</Text>
        </div>
      );
    },
  },
  {
    key: 'lastTransaction',
    title: 'Última transação',
    dataIndex: 'lastTransaction',
    width: '20%',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function Terminals() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>([]);

  // payfac-1 = intermediador logado no portal (mock)
  const LOCKED_PAYFAC = 'payfac-1';

  const [drawerConfig, setDrawerConfig] = useState<{
    initialTerminals?: string[];
    initialOperation?: OperationType;
    initialStep?: number;
    payfacLocked?: string;
  }>({});

  const [detalhesOpen, setDetalhesOpen] = useState(false);
  const [detalhesAction, setDetalhesAction] = useState<'desassociar' | 'associar-outro' | null>(null);
  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>(null);

  function handleRowClick(record: Terminal) {
    setSelectedTerminal(record);
    setDetalhesAction(null);
    setDetalhesOpen(true);
    setSelectedRowKeys([]);
  }

  const columns = useMemo((): ColumnDefinition<Terminal>[] => {
    const transacionalTooltipTitle = (
      <span className="col-header-with-info">
        Status transacional
        <Tooltip
          label="Indica se o terminal transacionou nos últimos 15 dias. 'Inativa' não significa que o terminal está impedido de transacionar — apenas que não houve transações registradas nesse período."
          placement="top"
        >
          <span className="col-header-info-icon">
            <Icon icon={Info} size={14} weight="regular" />
          </span>
        </Tooltip>
      </span>
    );
    const statusTransacional: ColumnDefinition<Terminal> = {
      key: 'statusTransacional',
      title: transacionalTooltipTitle as unknown as string,
      dataIndex: 'lastTransaction',
      width: '20%',
      render: (_value, record) => {
        if (record.status !== 'Alocada') return '—';
        const active = isTransactionActive(record.lastTransaction);
        const pill = (
          <Pills
            label={active ? 'Ativa' : 'Inativa'}
            state={active ? 'success' : 'default'}
            size="sm"
          />
        );
        return pill;
      },
    };

    const result = [...BASE_COLUMNS];
    result.splice(2, 0, statusTransacional);
    return result;
  }, []);

  // Busca principal
  const [searchType, setSearchType] = useState<SearchType>('serial');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const currentSearchType = SEARCH_TYPE_OPTIONS.find((o) => o.value === searchType)!;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchBarRef.current && !searchBarRef.current.contains(e.target as Node)) {
        setTypeDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [filterOpen, setFilterOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<TerminalStatus[]>([]);
  const [activeFilters, setActiveFilters] = useState<TerminalStatus[]>([]);
  const [statusItemOpen, setStatusItemOpen] = useState(false);
  const [pendingTransacional, setPendingTransacional] = useState<('ativa' | 'inativa')[]>([]);
  const [activeTransacional, setActiveTransacional] = useState<('ativa' | 'inativa')[]>([]);
  const [transacionalItemOpen, setTransacionalItemOpen] = useState(false);
  const [pendingOwner, setPendingOwner] = useState<TerminalOwner[]>([]);
  const [activeOwner, setActiveOwner] = useState<TerminalOwner[]>([]);
  const [ownerItemOpen, setOwnerItemOpen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  function handleFilterToggle() {
    if (!filterOpen) {
      setPendingFilters(activeFilters);
      setPendingTransacional(activeTransacional);
      setPendingOwner(activeOwner);
      setStatusItemOpen(false);
      setTransacionalItemOpen(false);
      setOwnerItemOpen(false);
    }
    setFilterOpen((o) => !o);
  }

  function handleFilterApply() {
    setActiveFilters(pendingFilters);
    setActiveTransacional(pendingTransacional);
    setActiveOwner(pendingOwner);
    setFilterOpen(false);
    setPage(1);
  }

  function handleFilterClear() {
    setPendingFilters([]);
    setActiveFilters([]);
    setPendingTransacional([]);
    setActiveTransacional([]);
    setPendingOwner([]);
    setActiveOwner([]);
    setFilterOpen(false);
    setPage(1);
  }

  function handleFilterClose() {
    setFilterOpen(false);
    setPendingFilters(activeFilters);
    setPendingTransacional(activeTransacional);
    setPendingOwner(activeOwner);
  }

  function togglePending(status: TerminalStatus) {
    setPendingFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  }

  function toggleTransacional(val: 'ativa' | 'inativa') {
    setPendingTransacional((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );
  }

  function toggleOwner(owner: TerminalOwner) {
    setPendingOwner((prev) =>
      prev.includes(owner) ? prev.filter((o) => o !== owner) : [...prev, owner],
    );
  }

  const filteredData = useMemo(() => {
    const byOwnerFilter = activeOwner.length > 0
      ? ALL_TERMINALS.filter((t) => activeOwner.includes(t.owner))
      : ALL_TERMINALS;

    const byFilter = activeFilters.length > 0
      ? byOwnerFilter.filter((t) => activeFilters.includes(t.status))
      : byOwnerFilter;

    const byTransacional = activeTransacional.length > 0
      ? byFilter.filter((t) => {
          const statusKey = isTransactionActive(t.lastTransaction) ? 'ativa' : 'inativa';
          return activeTransacional.includes(statusKey);
        })
      : byFilter;

    const q = searchQuery.trim().toLowerCase();
    if (!q) return byTransacional;

    if (searchType === 'serial')    return byTransacional.filter((t) => t.serialNumber.toLowerCase().includes(q));
    if (searchType === 'lojista')   return byTransacional.filter((t) => t.seller.toLowerCase().includes(q));
    if (searchType === 'cnpj')      return byTransacional.filter((t) => sellerCnpjFor(t.sellerId).toLowerCase().includes(q));
    return byTransacional.filter((t) => t.sellerId.toLowerCase().includes(q));
  }, [activeOwner, activeFilters, activeTransacional, searchQuery, searchType]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const hasActiveFilters = activeFilters.length > 0 || activeTransacional.length > 0 || activeOwner.length > 0;

  const selectedTerminals = useMemo(
    () => ALL_TERMINALS.filter((t) => selectedRowKeys.includes(t.id)),
    [selectedRowKeys],
  );

  function handlePageSizeChange(size: number) {
    setPageSize(size);
    setPage(1);
  }

  function openDrawerFromActionBar(actionKey: BulkActionKey) {
    const def = BULK_ACTION_DEFS[actionKey];
    setDrawerConfig({
      initialTerminals: selectedTerminals.map((t) => t.serialNumber),
      initialOperation: def.operation,
      initialStep: def.drawerStep,
      payfacLocked: LOCKED_PAYFAC,
    });
    setDrawerOpen(true);
  }

  return (
    <PortalPayfacsLayout activeItem="terminais">
      <main className="content">
          <div className="content-inner">
            <PageHeading
              title="Terminais"
            />

            <div className="filter-bar">
              {/* Campo de busca com select de tipo */}
              <div className="search-input-wrapper" ref={searchBarRef}>
                {/* Select de tipo inline */}
                <div className="search-type-select">
                  <button
                    className="search-type-btn"
                    onClick={() => setTypeDropdownOpen((v) => !v)}
                  >
                    <Text variant="label-sm">{currentSearchType.label}</Text>
                    <Icon icon={CaretDown} size={14} weight="bold" />
                  </button>
                  {typeDropdownOpen && (
                    <ul className="search-type-dropdown">
                      {SEARCH_TYPE_OPTIONS.map((opt) => (
                        <li
                          key={opt.value}
                          className={`search-type-option${searchType === opt.value ? ' search-type-option--active' : ''}`}
                          onMouseDown={() => {
                            setSearchType(opt.value);
                            setTypeDropdownOpen(false);
                            setSearchQuery('');
                          }}
                        >
                          <Text variant="body-sm">{opt.label}</Text>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="search-divider" />

                <Icon icon={MagnifyingGlass} size={16} weight="regular" className="search-icon" />
                <input
                  className="search-input-native"
                  placeholder={currentSearchType.placeholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <Filter
                label="Filtro"
                isOpen={filterOpen}
                onToggle={handleFilterToggle}
                hasActiveFilters={hasActiveFilters}
                className={hasActiveFilters ? 'filter-has-active' : undefined}
              >
                <FilterDropdown
                  title="Filtros"
                  onClear={handleFilterClear}
                  onClose={handleFilterClose}
                  onSubmit={handleFilterApply}
                  submitDisabled={pendingFilters.length === 0 && pendingTransacional.length === 0 && pendingOwner.length === 0}
                >
                  <FilterDropdownItem
                    label="Status"
                    isOpen={statusItemOpen}
                    onToggle={() => setStatusItemOpen((o) => !o)}
                    isFilled={pendingFilters.length > 0}
                    onClear={() => setPendingFilters([])}
                  >
                    <div className="filter-status-list">
                      {(['Em estoque', 'Alocada'] as TerminalStatus[]).map((status) => (
                        <label key={status} className="filter-status-option">
                          <Checkbox
                            checked={pendingFilters.includes(status)}
                            onChange={() => togglePending(status)}
                          />
                          <Pills label={STATUS_LABEL[status]} state={STATUS_PILL[status]} size="sm" />
                        </label>
                      ))}
                    </div>
                  </FilterDropdownItem>
                  <FilterDropdownItem
                    label="Status transacional"
                    isOpen={transacionalItemOpen}
                    onToggle={() => setTransacionalItemOpen((o) => !o)}
                    isFilled={pendingTransacional.length > 0}
                    onClear={() => setPendingTransacional([])}
                  >
                    <div className="filter-status-list">
                      {(['ativa', 'inativa'] as const).map((val) => (
                        <label key={val} className="filter-status-option">
                          <Checkbox
                            checked={pendingTransacional.includes(val)}
                            onChange={() => toggleTransacional(val)}
                          />
                          <Pills
                            label={val === 'ativa' ? 'Ativa' : 'Inativa'}
                            state={val === 'ativa' ? 'success' : 'default'}
                            size="sm"
                          />
                        </label>
                      ))}
                    </div>
                  </FilterDropdownItem>
                  <FilterDropdownItem
                    label="Proprietário"
                    isOpen={ownerItemOpen}
                    onToggle={() => setOwnerItemOpen((o) => !o)}
                    isFilled={pendingOwner.length > 0}
                    onClear={() => setPendingOwner([])}
                  >
                    <div className="filter-status-list">
                      {(['Barte', 'Própria'] as TerminalOwner[]).map((owner) => (
                        <label key={owner} className="filter-status-option">
                          <Checkbox
                            checked={pendingOwner.includes(owner)}
                            onChange={() => toggleOwner(owner)}
                          />
                          <Pills
                            label={owner}
                            state="default"
                            variant={owner === 'Barte' ? 'fill' : 'light'}
                            size="sm"
                          />
                        </label>
                      ))}
                    </div>
                  </FilterDropdownItem>
                </FilterDropdown>
              </Filter>

              <div className="filter-bar-actions">
                <Button
                  variant="outline"
                  size="sm"
                  iconLeft={<Icon icon={Export} size={16} weight="regular" />}
                  onClick={() => setShowExportModal(true)}
                >
                  Exportar
                </Button>
              </div>
            </div>

            <div className="content-table payfac-terminals-table">
              <div className="content-table-scroll">
                <Table<Terminal>
                  data={pagedData}
                  columns={columns}
                  rowKey={(r) => r.id}
                  size="sm"
                  emptyText="Nenhum terminal encontrado."
                  onRowClick={handleRowClick}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                  }}
                  pagination={{
                    currentPage: page,
                    totalPages,
                    totalItems: filteredData.length,
                    pageSize,
                    pageSizeOptions: [10, 25, 50],
                    onPageChange: setPage,
                    onPageSizeChange: handlePageSizeChange,
                  }}
                />
              </div>
            </div>
          </div>
      </main>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        totalCount={filteredData.length}
        selectedCount={selectedRowKeys.length}
      />

      <DrawerOperacoesEmLote
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        {...drawerConfig}
      />

      <DrawerDetalhesTerminal
        isOpen={detalhesOpen}
        onClose={() => { setDetalhesOpen(false); setDetalhesAction(null); }}
        terminal={selectedTerminal}
        initialStatusAction={detalhesAction}
      />

      {selectedRowKeys.length > 0 && (
        <div className="bulk-bar">
          <div className="bulk-bar-left">
            <Text variant="label-base" className="bulk-bar-count">
              {selectedRowKeys.length} {selectedRowKeys.length === 1 ? 'terminal' : 'terminais'} selecionado{selectedRowKeys.length === 1 ? '' : 's'}
            </Text>
          </div>

          <div className="bulk-bar-actions">
            <Button
              variant="outline"
              size="sm"
              iconLeft={<Icon icon={BULK_ACTION_DEFS['desvincular'].icon} size={16} weight="regular" />}
              onClick={() => openDrawerFromActionBar('desvincular')}
            >
              {BULK_ACTION_DEFS['desvincular'].label}
            </Button>
            <Button
              variant="fill"
              size="sm"
              iconLeft={<Icon icon={BULK_ACTION_DEFS['vincular-seller'].icon} size={16} weight="regular" />}
              onClick={() => openDrawerFromActionBar('vincular-seller')}
            >
              {BULK_ACTION_DEFS['vincular-seller'].label}
            </Button>
          </div>

          <div className="bulk-bar-right">
            <Button
              variant="outline"
              size="sm"
              className="bulk-bar-cancel"
              onClick={() => setSelectedRowKeys([])}
            >
              Cancelar
            </Button>
            <IconButton
              variant="outline"
              size="sm"
              aria-label="Limpar seleção"
              className="bulk-bar-close"
              onClick={() => setSelectedRowKeys([])}
            >
              <Icon icon={X} size={16} weight="bold" />
            </IconButton>
          </div>
        </div>
      )}
    </PortalPayfacsLayout>
  );
}
